import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { 
  Home, 
  Plus, 
  BarChart3, 
  Settings, 
  User,
  Menu,
  X,
  LogOut,
  Sun,
  Moon,
  LogIn,
  UserPlus
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import Toast from './Toast'

export default function Navigation() {
  const location = useLocation()
  const pathname = location.pathname
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [isDark, setIsDark] = useState(false)
  const { user, logout } = useAuth()

  // 根据时间自动判断主题
  const getAutoTheme = () => {
    const hour = new Date().getHours()
    return hour >= 6 && hour < 18 ? 'light' : 'dark'
  }

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme')
    let theme: string
    
    if (savedTheme) {
      theme = savedTheme
    } else {
      theme = getAutoTheme()
      localStorage.setItem('theme', theme)
    }
    
    if (theme === 'dark') {
      setIsDark(true)
      document.documentElement.setAttribute('data-theme', 'dark')
    } else {
      setIsDark(false)
      document.documentElement.removeAttribute('data-theme')
    }
  }, [])

  const toggleTheme = () => {
    const newTheme = !isDark
    setIsDark(newTheme)
    
    if (newTheme) {
      document.documentElement.setAttribute('data-theme', 'dark')
      localStorage.setItem('theme', 'dark')
    } else {
      document.documentElement.removeAttribute('data-theme')
      localStorage.setItem('theme', 'light')
    }
  }

  // 检测是否在Demo页面
  const isDemoPage = pathname.startsWith('/demo')

  const navigation = [
    { name: '首页', href: '/', icon: Home },
    { name: '新建策略', href: '/strategy/new', icon: Plus },
    { name: '策略列表', href: '/strategies', icon: BarChart3 },
    { name: '测试页面', href: '/test', icon: BarChart3 },
    { name: '设置', href: '/settings', icon: Settings },
    { name: '个人中心', href: '/profile', icon: User },
  ]

  const isActive = (href: string) => {
    if (href === '/') {
      return pathname === '/'
    }
    return pathname.startsWith(href)
  }

  return (
    <nav className="bg-white/10 backdrop-blur-md border-b border-white/20 shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <button
              onClick={() => {
                if (isDemoPage) {
                  setShowToast(true)
                } else {
                  window.location.href = '/'
                }
              }}
              className="flex items-center hover:opacity-80 transition-opacity"
            >
              <div className="w-8 h-8 bg-gradient-to-r from-violet-500 to-purple-600 rounded-lg flex items-center justify-center shadow-lg">
                <BarChart3 className="w-5 h-5 text-white" />
              </div>
              <span className="ml-2 text-lg sm:text-xl font-bold text-gradient">TideLab</span>
            </button>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {/* Theme Toggle for Desktop */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full transition-all duration-300 hover:bg-white/10"
              title={isDark ? '切换到明亮模式' : '切换到暗色模式'}
            >
              {isDark ? (
                <Sun className="w-5 h-5 text-yellow-300" />
              ) : (
                <Moon className="w-5 h-5 text-blue-500" />
              )}
            </button>
            {navigation.map((item) => {
              const Icon = item.icon
              return (
                <button
                  key={item.name}
                  onClick={() => {
                    if (isDemoPage) {
                      setShowToast(true)
                    } else {
                      window.location.href = item.href
                    }
                  }}
                  className={`flex items-center px-3 py-2 text-sm font-medium rounded-full transition-all duration-200 ${
                    isActive(item.href)
                      ? 'text-white bg-violet-500/20 border border-violet-500/30 shadow-lg'
                      : 'text-white/70 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {item.name}
                </button>
              )
            })}
            
            {/* User Menu */}
            {user ? (
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-violet-500/20 rounded-full flex items-center justify-center border border-violet-500/30">
                    <User className="w-4 h-4 text-violet-400" />
                  </div>
                  <span className="text-sm font-medium text-white">
                    {user.username || user.email}
                  </span>
                </div>
                <button
                  onClick={async () => {
                    try {
                      await logout()
                    } catch (error) {
                      // 忽略登出错误
                    }
                  }}
                  disabled={false}
                  className="flex items-center px-3 py-2 text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  登出
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => {
                    if (isDemoPage) {
                      if (window.confirm('即将离开Demo进入主页面，是否继续？')) {
                        window.location.href = '/login'
                      }
                    } else {
                      window.location.href = '/login'
                    }
                  }}
                  className="text-sm font-medium text-white/70 hover:text-white transition-colors"
                >
                  登录
                </button>
                <button
                  onClick={() => {
                    if (isDemoPage) {
                      if (window.confirm('即将离开Demo进入主页面，是否继续？')) {
                        window.location.href = '/register'
                      }
                    } else {
                      window.location.href = '/register'
                    }
                  }}
                  className="btn-primary text-sm"
                >
                  注册
                </button>
              </div>
            )}
          </div>

          {/* Mobile menu button with theme toggle */}
          <div className="md:hidden flex items-center space-x-2">
            {/* Mobile Theme Toggle in Header */}
            <div className="flex items-center space-x-2">
              <span className="text-xs text-white/70">
                {isDark ? '暗色' : '明亮'}
              </span>
              <button
                onClick={toggleTheme}
                className="p-2 rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-all duration-200"
                title={isDark ? '切换到明亮模式' : '切换到暗色模式'}
              >
                {isDark ? (
                  <Sun className="w-5 h-5 text-yellow-300" />
                ) : (
                  <Moon className="w-5 h-5 text-blue-500" />
                )}
              </button>
            </div>
            
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-all duration-200"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 bg-white/10 backdrop-blur-md border-t border-white/20">
            {navigation.map((item) => {
              const Icon = item.icon
              return (
                <button
                  key={item.name}
                  onClick={() => {
                    if (isDemoPage) {
                      setShowToast(true)
                      setIsMobileMenuOpen(false)
                    } else {
                      window.location.href = item.href
                      setIsMobileMenuOpen(false)
                    }
                  }}
                  className={`flex items-center px-3 py-2 text-base font-medium rounded-xl transition-all duration-200 ${
                    isActive(item.href)
                      ? 'text-white bg-violet-500/20 border border-violet-500/30'
                      : 'text-white/70 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {item.name}
                </button>
              )
            })}
            

            
            {/* Mobile User Menu */}
            {user ? (
              <>
                <div className="border-t border-white/20 pt-4 mt-4">
                  <div className="flex items-center px-3 py-2">
                    <div className="w-8 h-8 bg-violet-500/20 rounded-full flex items-center justify-center border border-violet-500/30">
                      <User className="w-4 h-4 text-violet-400" />
                    </div>
                    <span className="ml-3 text-base font-medium text-white">
                      {user.username || user.email}
                    </span>
                  </div>
                </div>
                <button
                  onClick={async () => {
                    try {
                      await logout()
                      setIsMobileMenuOpen(false)
                    } catch (error) {
                      // 忽略登出错误
                    }
                  }}
                  className="flex items-center justify-center w-full p-3 text-white/70 hover:text-white hover:bg-white/10 rounded-xl transition-all duration-200"
                  title="登出"
                >
                  <LogOut className="w-6 h-6" />
                </button>
              </>
            ) : (
              <div className="border-t border-white/20 pt-4 mt-4 flex space-x-2">
                <button
                  onClick={() => {
                    if (isDemoPage) {
                      if (window.confirm('即将离开Demo进入主页面，是否继续？')) {
                        window.location.href = '/login'
                        setIsMobileMenuOpen(false)
                      }
                    } else {
                      window.location.href = '/login'
                      setIsMobileMenuOpen(false)
                    }
                  }}
                  className="flex items-center justify-center flex-1 p-3 text-white/70 hover:text-white hover:bg-white/10 rounded-xl transition-all duration-200"
                  title="登录"
                >
                  <LogIn className="w-6 h-6" />
                </button>
                <button
                  onClick={() => {
                    if (isDemoPage) {
                      if (window.confirm('即将离开Demo进入主页面，是否继续？')) {
                        window.location.href = '/register'
                        setIsMobileMenuOpen(false)
                      }
                    } else {
                      window.location.href = '/register'
                      setIsMobileMenuOpen(false)
                    }
                  }}
                  className="flex items-center justify-center flex-1 p-3 bg-violet-500 hover:bg-violet-600 text-white rounded-xl transition-all duration-200"
                  title="注册"
                >
                  <UserPlus className="w-6 h-6" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Toast */}
      <Toast
        message="Demo仅做展示，详细功能请查看下方功能导航"
        isVisible={showToast}
        onClose={() => setShowToast(false)}
        duration={3000}
      />
    </nav>
  )
} 